import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'

type ArticleListingCardProps = {
    to: string
    category: string
    publishedAt: string
    formattedPublishedAt: string
    readingMinutesLabel: string
    title: string
    description: string
    imagePath: string
    readArticleLabel?: string
    loading?: 'lazy' | 'eager'
    fetchPriority?: 'high' | 'low'
    imageClassName?: string
    imageObjectClass?: string
    /** `lead` = featured overlay; `card` = grid tile. Default keeps legacy row layout. */
    variant?: 'row' | 'lead' | 'card'
}

type ArticleMetaProps = {
    category: string
    publishedAt: string
    formattedPublishedAt: string
    readingMinutesLabel: string
    className: string
    categoryClassName?: string
}

function ArticleMeta({
    category,
    publishedAt,
    formattedPublishedAt,
    readingMinutesLabel,
    className,
    categoryClassName,
}: ArticleMetaProps) {
    return (
        <div className={className}>
            <span className={categoryClassName}>{category}</span>
            <time dateTime={publishedAt}>{formattedPublishedAt}</time>
            <span>{readingMinutesLabel}</span>
        </div>
    )
}

type ListingImageProps = {
    src: string
    className: string
    loading?: 'lazy' | 'eager'
    fetchPriority?: 'high' | 'low'
}

function ListingImage({ src, className, loading = 'lazy', fetchPriority }: ListingImageProps) {
    return (
        <img
            src={src}
            alt=''
            className={className}
            width={1536}
            height={1024}
            loading={loading}
            fetchPriority={fetchPriority}
            decoding='async'
        />
    )
}

export function ArticleListingCard({
    to,
    category,
    publishedAt,
    formattedPublishedAt,
    readingMinutesLabel,
    title,
    description,
    imagePath,
    readArticleLabel,
    loading = 'lazy',
    fetchPriority,
    imageClassName,
    imageObjectClass,
    variant = 'row',
}: ArticleListingCardProps) {
    const meta = {
        category,
        publishedAt,
        formattedPublishedAt,
        readingMinutesLabel,
    }
    const imageClass = (fallback: string) => imageClassName ?? cn(fallback, imageObjectClass)

    if (variant === 'lead') {
        return (
            <LocaleLink
                to={to}
                className='group relative block min-h-[clamp(340px,44vw,520px)] overflow-hidden rounded-article-panel text-article-shell-fg no-underline'
            >
                <ListingImage
                    src={imagePath}
                    className={imageClass(
                        'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]',
                    )}
                    loading={loading}
                    fetchPriority={fetchPriority}
                />
                <div
                    className='pointer-events-none absolute inset-0 bg-[color-mix(in_srgb,var(--article-shell)_35%,transparent)]'
                    aria-hidden
                />
                <div
                    className='pointer-events-none absolute inset-0 bg-gradient-to-t from-[color-mix(in_srgb,var(--article-shell)_100%,transparent)] from-[12%] via-[color-mix(in_srgb,var(--article-shell)_90%,transparent)] via-[48%] to-[color-mix(in_srgb,var(--article-shell)_25%,transparent)]'
                    aria-hidden
                />
                <div className='relative flex min-h-[clamp(340px,44vw,520px)] flex-col justify-end p-[clamp(20px,3.4vw,40px)]'>
                    <ArticleMeta
                        {...meta}
                        className='mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-article-cream'
                        categoryClassName='rounded-full bg-article-cream px-3 py-1 font-bold tracking-[0.06em] text-article-fg'
                    />
                    <h2 className='m-0 max-w-[24ch] text-[clamp(26px,3.6vw,50px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]'>
                        {title}
                    </h2>
                    <p className='mb-0 mt-3 max-w-[54ch] text-[clamp(15px,1.4vw,18px)] font-medium leading-snug text-article-cream'>
                        {description}
                    </p>
                    {readArticleLabel ? (
                        <span className='mt-4 text-sm font-extrabold text-article-flame'>{readArticleLabel} →</span>
                    ) : null}
                </div>
            </LocaleLink>
        )
    }

    if (variant === 'card') {
        return (
            <LocaleLink
                to={to}
                className='group flex h-full flex-col gap-3 text-article-fg no-underline transition-colors'
            >
                <div className='overflow-hidden rounded-article-card'>
                    <ListingImage
                        src={imagePath}
                        className={imageClass(
                            'aspect-[3/2] h-[clamp(160px,18vw,220px)] w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]',
                        )}
                        loading={loading}
                        fetchPriority={fetchPriority}
                    />
                </div>
                <div className='flex min-w-0 flex-col gap-2'>
                    <ArticleMeta
                        {...meta}
                        className='flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-article-muted-soft'
                        categoryClassName='text-article-flame'
                    />
                    <h2 className='m-0 text-[clamp(19px,1.8vw,23px)] font-bold leading-[1.14] tracking-[-0.025em] transition-colors group-hover:text-article-flame'>
                        {title}
                    </h2>
                    <p className='m-0 text-[15px] leading-[1.5] text-article-muted'>{description}</p>
                </div>
            </LocaleLink>
        )
    }

    return (
        <LocaleLink
            to={to}
            className='group flex flex-col gap-3 py-5 text-article-fg no-underline transition-colors hover:text-article-flame sm:flex-row sm:items-start sm:gap-5'
        >
            <ListingImage
                src={imagePath}
                className={imageClass(
                    'aspect-[3/2] w-full rounded-[14px] object-cover sm:h-[132px] sm:w-[198px] sm:shrink-0',
                )}
                loading={loading}
                fetchPriority={fetchPriority}
            />
            <div className='flex min-w-0 flex-col gap-2'>
                <ArticleMeta
                    {...meta}
                    className='flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-article-muted-soft'
                />
                <h2 className='m-0 text-base font-semibold leading-snug md:text-[17px]'>{title}</h2>
                <p className='m-0 max-w-[52ch] text-[15px] leading-[1.65] text-article-muted'>{description}</p>
                {readArticleLabel ? (
                    <span className='text-sm font-semibold text-article-flame'>{readArticleLabel}</span>
                ) : null}
            </div>
        </LocaleLink>
    )
}
